import { partition } from 'lodash';
import { DateTime } from 'luxon';
import { SubmitOfferFormData } from 'src/components/offer/SectionOffer';
import { fromJsonArray } from 'src/core/utils';
import { Address, BasicAddress } from 'src/entities/basic-types/Address';
import AppliedVoucher from 'src/entities/cart/response/AppliedVoucher';
import CalculatedVolumeDiscount from 'src/entities/cart/response/CalculatedVolumeDiscount';
import OrderProduct from 'src/entities/cart/response/OrderProduct';
import OrderProductListGrouped from 'src/entities/cart/response/OrderProductListGrouped';
import { amountIn, OrderSummary } from 'src/entities/cart/response/OrderSummary';
import CustomerPaymentMethod from 'src/entities/customer/CustomerPaymentMethod';
import { ProductGroupSlug } from 'src/entities/product-group/ProductGroup';
import Product from 'src/entities/products/Product';
import VolumeDiscount from 'src/entities/volume-discount/VolumeDiscount';

export interface InstallmentPlan {
    rates: Rate[];
    totalAmount: number;
    currencyIso: string;
}

interface Rate {
    date: DateTime;
    localizedDescription: string;
    grossPrice: number;
    nettPrice: number;
    vat: number;
    currencyIso: string;
}

interface ExtraField {
    localizedLabel: string;
    name: string;
    type: 'check' | 'select';
    // Check presents text in modal
    localizedSubject?: string;
    subjectResource?: string;
    // Select
    options?: Option[];
}

interface Option {
    value: string;
    localizedLabel: string;
}
function sortPaymentMethods(a: CustomerPaymentMethod, b: CustomerPaymentMethod): number {
    const nameA = a.normalizedName.toUpperCase();
    const nameB = b.normalizedName.toUpperCase();
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
}

export interface ShippingOption {
    supplierId: number;
    name: string;
    unit: 'CART_VALUE' | 'PIECE' | 'WEIGHT' | string;
    price: number;
    /** R27-5: the option including VAT (price is net); undefined on older servers. */
    priceGross?: number;
}

/** R45: the shipping cost the calc resolved — EXACT for one carrier, FROM = "ab" the cheapest. */
export interface ShippingEstimate {
    mode: 'EXACT' | 'FROM';
    supplierId: number;
    amountNet: number;
    amountGross: number;
}

/** R45: how a summary shows the shipping cost; R56-10: "none" = only articles without shipping ("—"). */
export type ShippingDisplay = { kind: 'free' } | { kind: 'none' } | { kind: 'from' | 'exact'; amount: number };

export default class ShoppingCart {
    cartAmount: number;
    totalDiscount: number;
    /** R27-5: the discount in both modes (totalDiscount follows the cart's own mode). */
    totalDiscountNet?: number;
    totalDiscountGross?: number;
    /** R49-10: what the Sonderpreise save against the list prices — already inside the prices, never deducted again. */
    specialPriceSavingNet: number;
    specialPriceSavingGross: number;
    deliveryAmount: number;
    /** R25-3: delivery net of VAT (deliveryAmount is gross); undefined on older servers. */
    deliveryAmountNet?: number;
    totalAmount: number;
    totalTaxAmount: number;
    paymentMethodIdentifier?: string;
    currencyIso: string;
    gross: boolean;
    sendMailToCustomer?: boolean;
    sourceDepot?: string;
    orderProductListGrouped: OrderProductListGrouped[];
    containsOrderDirectlyProduct: boolean;
    possiblePaymentMethods: CustomerPaymentMethod[];
    paritalPaymentMethods: CustomerPaymentMethod[];
    paymentMethods: CustomerPaymentMethod[];
    installmentPlan?: InstallmentPlan;
    extraFields: ExtraField[];
    usedCredit: number;
    creditMinimumCartValue: number;
    fromLandingPage: boolean;
    fromLandingPageId?: string;
    fromLandingPageRebateType?: string;
    newCustomer: boolean;
    useCredit: boolean;
    publicId?: string;
    customerId?: number;
    originUserId?: number;
    possibleRebate: number;
    /** R44: the rebate granted from the partner's own commission (negative, 0 without) — its line never rides the products. */
    grantedRebateNet: number;
    grantedRebateGross: number;
    /** R26-1: the cart carries a rebate the buying partner granted from their own commission. */
    selfRebate: boolean;
    /** R26-1: the partner's checkout choice echoed by the server; R54-9: deducted only on an explicit true. */
    selfRebateChoice?: boolean;
    billingAddress?: Address | BasicAddress;
    deliveryAddress?: Address | BasicAddress;
    selectedSourceDepot?: string;
    selectedSendMailToCustomer?: boolean;
    skipDefaultVendorOverride?: boolean;
    appliedVoucher: AppliedVoucher | null;
    /** "REBATE_HIGHER" when the sent voucher lost against the Mengenrabatt (D-U5-4); null otherwise. */
    voucherNotAppliedReason: string | null;
    /** R55: the invoice country has no usable Guthabenartikel — the credit is not redeemed. */
    creditArticleMissing: boolean;
    /** WS-3 (Runde 2): the selectable outbound shipping options, computed server-side per calc. */
    shippingOptions: ShippingOption[];
    /** The confirmed choice echoed by the calc; rides every follow-up request. */
    shippingSupplierId: number | null;
    /** R22-5: set when the cart ships via the extra-handling team — no shipping option to pick. */
    extraHandlingKind: 'SHIPPING' | 'PRECONFIGURATION' | 'ON_SITE' | null;
    /** R45: null = no carrier options (the delivery amount is shown as is). */
    shippingEstimate: ShippingEstimate | null;
    /** R45: shippingSupplierId is the calc's preselection (address favourite / only carrier), not a choice. */
    shippingSupplierPreselected: boolean;
    /** R45: the billing address's default payment method this cart offers. */
    preselectedPaymentMethodIdentifier: string | null;
    /** R66: SEPA is enabled for the billing country (also before the mandate is confirmed, whatever the amount limit); null on older servers. */
    directDebitOffered: boolean | null;
    /** R56-2: the figures the server computes over the calculated lines; null on older servers. */
    summary: OrderSummary | null;

    constructor(json: any) {
        this.cartAmount = json.cartAmount;
        this.totalDiscount = json.totalDiscount;
        this.totalDiscountNet = json.totalDiscountNet;
        this.totalDiscountGross = json.totalDiscountGross;
        this.specialPriceSavingNet = json.specialPriceSavingNet ?? 0;
        this.specialPriceSavingGross = json.specialPriceSavingGross ?? 0;
        this.deliveryAmount = json.deliveryAmount;
        this.deliveryAmountNet = json.deliveryAmountNet;
        this.totalAmount = json.totalAmount;
        this.totalTaxAmount = json.totalTaxAmount;
        this.paymentMethodIdentifier = json.paymentMethodIdentifier;
        this.currencyIso = json.currencyIso4217 || json.currencyIso;
        this.gross = json.gross;
        this.sendMailToCustomer = json.sendMailToCustomer ?? false;
        this.sourceDepot = json.sourceDepot;
        this.orderProductListGrouped = fromJsonArray(OrderProductListGrouped, json.orderProductListGrouped).filter(
            orderProductListGroup => orderProductListGroup.products.length > 0
        );
        this.containsOrderDirectlyProduct = json.containsOrderDirectlyProduct ?? false;
        this.shippingOptions = json.shippingOptions ?? [];
        this.shippingSupplierId = json.shippingSupplierId ?? null;
        this.extraHandlingKind = json.extraHandlingKind ?? null;
        this.shippingEstimate = json.shippingEstimate ?? null;
        this.shippingSupplierPreselected = json.shippingSupplierPreselected ?? false;
        this.preselectedPaymentMethodIdentifier = json.preselectedPaymentMethodIdentifier ?? null;
        this.directDebitOffered = json.directDebitOffered ?? null;
        this.summary = json.summary ?? null;
        this.extraFields = (json.extraFields ?? []).filter(
            extraField => extraField.type === 'check' || extraField.type === 'select'
        );

        this.possiblePaymentMethods = fromJsonArray(CustomerPaymentMethod, json.possiblePaymentMethods);
        const [paritalPaymentMethods, paymentMethods] = partition(this.possiblePaymentMethods, p => p.isPartial());
        this.paritalPaymentMethods = paritalPaymentMethods.sort(sortPaymentMethods);
        this.paymentMethods = paymentMethods.sort(sortPaymentMethods);

        if (json.installmentPlan) {
            this.installmentPlan = {
                rates: json.installmentPlan.rates.map(rate => ({
                    ...rate,
                    date: DateTime.fromISO(rate.date),
                    currencyIso: rate.currencyIso || 'EUR',
                })),
                totalAmount: json.installmentPlan.totalAmount,
                currencyIso: json.installmentPlan.currencyIso || 'EUR',
            };
        }
        this.usedCredit = json.usedCredit ?? 0;
        this.creditMinimumCartValue = json.creditMinimumCartValue ?? 0;
        this.fromLandingPage = json.fromLandingPage ?? false;
        this.fromLandingPageId = json.fromLandingPageId;
        this.fromLandingPageRebateType = json.fromLandingPageRebateType ?? 'none';
        this.newCustomer = json.newCustomer ?? false;
        this.useCredit = json.useCredit;
        this.publicId = json.publicId;
        this.possibleRebate = json.possibleRebate ?? 0;
        this.grantedRebateNet = json.grantedRebateNet ?? 0;
        this.grantedRebateGross = json.grantedRebateGross ?? 0;
        // R54-9: a cart still in the session from before R54 carries the old server's implicit tick — ignore it
        const echoTrusted = json.selfRebateEchoTrusted === true;
        this.selfRebate = echoTrusted && this.grantedRebateNet !== 0;
        const choice = echoTrusted ? json.additionalProperties?.selfRebate : undefined;
        this.selfRebateChoice = choice === 'true' ? true : choice === 'false' ? false : undefined;
        this.customerId = json.customerId;
        this.originUserId = json.originUserId;

        if (json.billingAddress) {
            this.billingAddress = json.billingAddress.id
                ? new Address(json.billingAddress)
                : new BasicAddress(json.billingAddress);
        }
        if (json.deliveryAddress) {
            this.deliveryAddress = json.deliveryAddress.id
                ? new Address(json.deliveryAddress)
                : new BasicAddress(json.deliveryAddress);
        }
        this.selectedSourceDepot = json.selectedSourceDepot;
        this.selectedSendMailToCustomer = json.selectedSendMailToCustomer;
        this.skipDefaultVendorOverride = json.skipDefaultVendorOverride;
        this.appliedVoucher = json.appliedVoucher ? new AppliedVoucher(json.appliedVoucher) : null;
        this.voucherNotAppliedReason = json.voucherNotAppliedReason ?? null;
        this.creditArticleMissing = json.creditArticleMissing === true;
    }

    /** R45: "kostenlos" only when the shipping really costs nothing; "ab" until a carrier is resolved. */
    public shippingDisplay(gross: boolean): ShippingDisplay {
        if (this.summary) {
            const amount = amountIn(this.summary.shipping, gross);
            if (this.summary.shippingMode === 'NOT_REQUIRED') return { kind: 'none' };
            if (amount === 0) return { kind: 'free' };
            return { kind: this.summary.shippingMode === 'FROM' ? 'from' : 'exact', amount };
        }
        if (this.shippingEstimate?.mode === 'FROM') {
            const from = gross ? this.shippingEstimate.amountGross : this.shippingEstimate.amountNet;
            return from > 0 ? { kind: 'from', amount: from } : { kind: 'free' };
        }
        if (this.deliveryAmount === 0) return { kind: 'free' };
        return { kind: 'exact', amount: gross ? this.deliveryAmount : this.deliveryAmountNet ?? this.deliveryAmount };
    }

    /** The granted rebate (a negative figure) in the requested mode. */
    public grantedRebateIn(gross: boolean): number {
        return gross ? this.grantedRebateGross : this.grantedRebateNet;
    }

    public get isOffer(): boolean {
        return this.publicId != null && this.customerId === this.originUserId;
    }

    public get itemCount(): number {
        return this.getFlatProductList().length;
        // return this.getFlatProductList().reduce((acc, cur) => (acc += cur.quantity), 0);
    }

    public getFlatProductList(): OrderProduct[] {
        return this.orderProductListGrouped.flatMap(group => group.products);
    }

    /** The volume discount (a negative figure) in the requested mode. */
    public discountIn(gross: boolean): number {
        const exact = gross ? this.totalDiscountGross : this.totalDiscountNet;
        return exact ?? this.totalDiscount;
    }

    /** R54-9: the volume discount without the own commission (a negative figure) — the cart never deducts that. */
    public volumeDiscountIn(gross: boolean): number {
        return this.discountIn(gross) - this.grantedRebateIn(gross);
    }

    /** R49-10: the Sonderpreis saving (a positive figure) in the requested mode. */
    public specialPriceSavingIn(gross: boolean): number {
        return gross ? this.specialPriceSavingGross : this.specialPriceSavingNet;
    }

    /**
     * Returns an array of calculated discounts within the shopping cart
     * for products supported in volume discounts
     */
    public getCalculatedVolumeDiscounts(volumeDiscounts: VolumeDiscount[]): CalculatedVolumeDiscount[] {
        return this.orderProductListGrouped.reduce(
            (result: CalculatedVolumeDiscount[], productGroup: OrderProductListGrouped) => {
                const calculatedVolumeDiscount = productGroup.getCalculatedVolumeDiscount(volumeDiscounts);
                if (calculatedVolumeDiscount) result.push(calculatedVolumeDiscount);
                return result;
            },
            []
        );
    }

    public getQuantityOfArticleGroup(articleGroupNormalizedName: string, targetUnit?: string): number {
        return this.orderProductListGrouped
            .filter(group => group.normalizedName === articleGroupNormalizedName)
            .reduce((acc, cur) => (acc += cur.getTotalQuantity(targetUnit)), 0);
    }

    public isEmpty(): boolean {
        return this.orderProductListGrouped.length <= 0;
    }

    public containsUnfinishedProductCustomFields(): boolean {
        return this.orderProductListGrouped.some(group => group.containsUnfinishedProductCustomFields());
    }

    public static emptyCart(): ShoppingCart {
        return new ShoppingCart({
            cartAmount: 0,
            totalDiscount: 0,
            deliveryAmount: 0,
            totalAmount: 0,
            gross: false,
        });
    }

    public hasInstallmentPlan(): boolean {
        return this.installmentPlan !== undefined;
    }

    public hasTabsWithoutMachine(): boolean {
        const hasTabs = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1111');
        const hasMachine = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1211');
        return hasTabs && !hasMachine;
    }

    public hasMachineWithoutTabs(): boolean {
        const hasTabs = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1111');
        const hasMachine = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1211');
        return hasMachine && !hasTabs;
    }

    public getMissingProductDependency(): ProductGroupSlug | undefined {
        const hasTabs = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1111');
        const hasMachine = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1211');

        if (hasTabs && !hasMachine) return 'machines';
        if (hasMachine && !hasTabs) return 'coffeeTimeTabs';
    }

    public containsVariationsOfProduct(product: Product): boolean {
        const variants = product.variants.map(v => v.articleNumber);
        return this.getFlatProductList().some(p => variants.includes(p.ffProductNumber));
    }

    static offerFormData(cart?: ShoppingCart): SubmitOfferFormData {
        return {
            customerId: cart?.customerId ?? ('' as any),
            deliveryAddress: cart?.deliveryAddress,
            billingAddress: cart?.billingAddress,
            publicId: cart?.publicId,
            selectedSourceDepot: (cart?.selectedSourceDepot as any) ?? 'Lager',
            extra: {},
            additionalProperties: {
                officeProfiRebate: 0,
            },
            note: '',
        };
    }
}
